// Small helpers shared by the expense pages: labels, evidence badge, approval explanation, journal preview, decision buttons.
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Json } from '@/lib/api';
import { fileUrl } from '@/lib/supabase';
import { label } from '@/lib/format';
import { Badge, Button, Confirm, Money } from '@/components/ui';

export const PAID_VIA: Record<string, string> = { firm_money_account: 'Firm account', person: 'Paid by a person', unpaid: 'Not yet paid' };
export const PAY_MODES = ['bank_transfer', 'upi', 'cheque', 'card', 'cash', 'gateway'] as const;
export const EVIDENCE_TEXT: Record<string, string> = { missing: 'Evidence missing', complete: 'Evidence complete', declared: 'Declared (no bill)' };

export const newRequestKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function EvidenceBadge({ status }: { status?: string | null }) {
  return <Badge status={status ?? 'missing'}>{EVIDENCE_TEXT[status ?? 'missing'] ?? label(status)}</Badge>;
}

export function paidViaText(e: Json): string {
  if (e.paid_via === 'firm_money_account') return `${e.money_account?.name ?? e.money_account_name ?? 'Firm account'}${e.pay_mode ? ` · ${label(e.pay_mode)}` : ''}`;
  if (e.paid_via === 'person') return `${e.claimant?.display_name ?? e.claimant ?? 'Person'}${e.pay_mode ? ` · ${label(e.pay_mode)}` : ''}`;
  return 'Not yet paid';
}

/** Mirror of policy_for(): which approval policy an expense would hit, and what that means. */
export function explainApproval(policies: Json[], amount: number, ctx: Json, autoPost: boolean): { needs: boolean; policy: Json | null; text: string } {
  const matches = policies
    .filter((p) => p.active !== false && p.doc_type === 'expense' && amount >= Number(p.min_amount ?? 0) && (p.max_amount == null || amount <= Number(p.max_amount)))
    .filter((p) => { const c = p.condition; if (!c || typeof c !== 'object' || Object.keys(c).length === 0) return true; return Object.entries(c).every(([k, v]) => String(ctx[k] ?? '') === String(v)); })
    .sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100) || Number(b.min_amount ?? 0) - Number(a.min_amount ?? 0));
  const policy = matches[0] ?? null;
  if (!policy || policy.auto_approve) {
    return { needs: false, policy, text: policy ? `"${policy.name}": approved on submit${autoPost ? ' and posted straight away' : ''}.` : `No approval policy matches: approved on submit${autoPost ? ' and posted straight away' : ''}.` };
  }
  return { needs: true, policy, text: `"${policy.name}": needs ${policy.required_role === 'admin' ? 'an admin' : 'an approver'} who did not create it${autoPost ? '; it posts automatically once approved' : ''}.` };
}

export function JournalPreview({ lines, title = 'This will post' }: { lines?: Json[] | null; title?: ReactNode }) {
  if (!lines || lines.length === 0) return null;
  return (
    <div>
      <div className="field-label">{title}</div>
      <div className="journal-preview">
        {lines.map((l, i) => (
          <div key={i} className="row" style={{ justifyContent: 'space-between' }}>
            <span>{l.debit != null && Number(l.debit) !== 0 ? 'Dr' : 'Cr'} {l.account ?? `${l.account_code ?? ''} ${l.account_name ?? ''}`}{l.party_name ? ` (${l.party_name})` : ''}</span>
            <Money value={l.debit != null && Number(l.debit) !== 0 ? l.debit : l.credit} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Approve / Reject (reason) / Hold (note) buttons with their confirmation dialogs. */
export function DecisionButtons({ onDecide, busy, disabled, hint, size }: { onDecide: (decision: 'approved' | 'rejected' | 'hold', reason?: string) => void; busy?: boolean; disabled?: boolean; hint?: ReactNode; size?: 'sm' }) {
  const [which, setWhich] = useState<null | 'rejected' | 'hold'>(null);
  return (
    <span className="actions">
      <Button variant="primary" size={size} busy={busy} disabled={disabled} onClick={() => onDecide('approved')}>Approve</Button>
      <Button variant="danger" size={size} disabled={disabled || busy} onClick={() => setWhich('rejected')}>Reject</Button>
      <Button size={size} disabled={disabled || busy} onClick={() => setWhich('hold')}>Hold</Button>
      {hint && <small className="muted">{hint}</small>}
      <Confirm open={which === 'rejected'} onClose={() => setWhich(null)} title="Reject" text="Tell the requester why. They can fix it and submit again." reason="Reason" danger busy={busy}
        onConfirm={(r) => { setWhich(null); onDecide('rejected', r); }} />
      <Confirm open={which === 'hold'} onClose={() => setWhich(null)} title="Put on hold" text="Leave a note about what you need before approving." reason="Note" busy={busy}
        onConfirm={(r) => { setWhich(null); onDecide('hold', r); }} />
    </span>
  );
}

/** Thumbnail of a stored image attachment (signed URL), opening the file on tap. */
export function AttachmentThumb({ a, onRemove }: { a: Json; onRemove?: () => void }) {
  const url = useQuery({ queryKey: ['file-url', a.bucket, a.storage_path], queryFn: () => fileUrl(a.bucket ?? 'attachments', a.storage_path), staleTime: 500_000, enabled: !!a.storage_path });
  const isImage = String(a.mime ?? '').startsWith('image/');
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <a href={url.data ?? '#'} target="_blank" rel="noreferrer" onClick={(e) => { if (!url.data) e.preventDefault(); }} title={a.filename}>
        {isImage && url.data ? <img className="thumb" src={url.data} alt={a.filename ?? 'receipt'} /> : <span className="thumb" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', padding: 4, textAlign: 'center' }}>{label(a.kind)}<br />{(a.filename ?? '').slice(0, 14)}</span>}
      </a>
      {onRemove && <button type="button" className="toast-x" aria-label="Remove" style={{ position: 'absolute', top: -6, right: -6, background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 999, width: 22, height: 22, lineHeight: '18px' }} onClick={onRemove}>×</button>}
    </div>
  );
}

/** "3 h ago" style text for waiting times. */
export function since(iso?: string | null, now = Date.now()): string {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60); if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
}
